'use client';

import { useEffect, useState, useRef } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-next';
import { PageProps } from 'libs/contentful/queries/page/props';
import { usePage } from '@/contexts/PageProvider';
import {
  getCustomQueryStringParametersItemsForClient,
  getCustomQspsItemsFromVariant,
  getCustomQspsItemsForVariantIndexMap,
} from 'utils/pageHelpers/customQueryStringParametersClient';
import { convertCustomQspsToObject } from 'utils/formatters';
import { replaceQueryParamsInUrl } from 'utils/linkTransformer';
import { isInternalLink } from 'utils/linkHelpers';
import { CustomQueryStringParameterItems } from 'libs/contentful/types';

interface ClientCustomQspsUpdaterProps {
  page: PageProps;
}

function sectionFallback(page: PageProps): CustomQueryStringParameterItems[] {
  const sectionItems = page.customQueryStringParameters?.customQueryStringParametersCollection?.items ?? [];
  const inlineItems = page.customQueryStringParametersCollection?.items ?? [];
  return sectionItems.length > 0 ? sectionItems : inlineItems;
}

function getPreviewVariantIndexes(): Record<string, number> {
  if (typeof window === 'undefined') return {};
  const win = window as unknown as Record<string, unknown>;
  const ninetailed = win.ninetailed as Record<string, unknown> | undefined;
  const plugins = ninetailed?.plugins as Record<string, unknown> | undefined;
  const preview = plugins?.preview as Record<string, unknown> | undefined;
  return (preview?.experienceVariantIndexes as Record<string, number>) ?? {};
}

/** All param keys that may be set by this section (any variant, any experience). Used to strip before applying new set. */
function getCustomQspsParamKeys(page: PageProps, sectionId: string): Set<string> {
  const section = page.customQueryStringParameters;
  const ninetailedData = page.ninetailedData?.get(sectionId);
  if (!section || !ninetailedData?.mappedExperiences?.length) {
    return new Set(Object.keys(convertCustomQspsToObject(sectionFallback(page))));
  }

  const keys = new Set<string>(Object.keys(convertCustomQspsToObject(sectionFallback(page))));
  for (const experience of ninetailedData.mappedExperiences) {
    const component = experience.components?.find(comp => 'id' in comp.baseline && comp.baseline.id === sectionId);
    if (!component) continue;
    const selectableVariants = [component.baseline, ...(component.variants ?? [])];
    for (const variant of selectableVariants) {
      const items = getCustomQspsItemsFromVariant(variant);
      if (items?.length) {
        Object.keys(convertCustomQspsToObject(items)).forEach(k => keys.add(k));
      }
    }
  }
  return keys;
}

/**
 * Updates custom query string parameters when Ninetailed preview variants change (client-side).
 * Keeps link hrefs and context (e.g. EmailCapture) in sync with the selected variant's QSPs.
 * Replaces the section's QSPs on links (does not merge) so switching variants correctly removes previous params.
 */
export function ClientCustomQspsUpdater({ page }: ClientCustomQspsUpdaterProps) {
  const [isReady, setIsReady] = useState(false);
  const ninetailed = useNinetailed();
  const { updateResolvedCustomQueryStringParameters } = usePage();
  const lastAppliedRef = useRef<string>('');

  useEffect(() => {
    const timer = setTimeout(() => setIsReady(true), 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isReady) return;

    const section = page.customQueryStringParameters;
    const sectionId = section?.sys?.id;
    const hasNinetailedData = sectionId && page.ninetailedData?.get(sectionId)?.mappedExperiences?.length;

    if (!sectionId || !hasNinetailedData || !ninetailed?.onSelectVariant || !page.ninetailedData) return;

    const ninetailedData = page.ninetailedData.get(sectionId);
    if (!ninetailedData?.mappedExperiences) return;

    const fallback = sectionFallback(page);
    const allCustomQspsKeys = getCustomQspsParamKeys(page, sectionId);

    const applyItems = (items: CustomQueryStringParameterItems[]) => {
      const paramsKey = JSON.stringify(items);
      if (lastAppliedRef.current === paramsKey) return;
      lastAppliedRef.current = paramsKey;

      const searchParams = new URLSearchParams(convertCustomQspsToObject(items));
      updateResolvedCustomQueryStringParameters?.(items);

      const links = document.querySelectorAll<HTMLAnchorElement>('a[href]');
      links.forEach(link => {
        const href = link.getAttribute('href');
        if (!href || !isInternalLink(href)) return;
        const newHref = replaceQueryParamsInUrl(href, allCustomQspsKeys, searchParams);
        if (newHref !== href) link.setAttribute('href', newHref);
      });
    };

    const unsubscribe = ninetailed.onSelectVariant(
      {
        baseline: { id: sectionId },
        experiences: ninetailedData.mappedExperiences,
      } as unknown as Parameters<typeof ninetailed.onSelectVariant>[0],
      state => {
        if (!state) return;
        const fromVariant = state.status === 'success' ? getCustomQspsItemsFromVariant(state.variant) : null;
        const previewIndexes = getPreviewVariantIndexes();
        const hasPreviewSelection = Object.keys(previewIndexes).length > 0;
        const fromPreview =
          fromVariant ??
          (hasPreviewSelection
            ? getCustomQspsItemsForVariantIndexMap(page, previewIndexes)
            : getCustomQueryStringParametersItemsForClient(page));
        applyItems(fromVariant ?? fromPreview ?? fallback);
      },
    );

    const previewIndexes = getPreviewVariantIndexes();
    const hasPreviewSelection = Object.keys(previewIndexes).length > 0;
    const initialItems = hasPreviewSelection
      ? getCustomQspsItemsForVariantIndexMap(page, previewIndexes)
      : getCustomQueryStringParametersItemsForClient(page);
    applyItems(initialItems);

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
      lastAppliedRef.current = '';
      updateResolvedCustomQueryStringParameters?.(null);
    };
  }, [page, ninetailed, isReady, updateResolvedCustomQueryStringParameters]);

  return null;
}
